export const ROLES = {
  ADMIN: 'admin',
  DEVELOPER: 'developer',
  VIEWER: 'viewer'
};

export const RESOURCE_LEVEL_PERMISSIONS = {
  READ: 'read',
  WRITE: 'write',
  ADMIN: 'admin'
};

export const RESOURCE_TYPES = {
  FRONTEND_APP: 'FRONTEND_APP',
  BACKEND_SERVICE: 'BACKEND_SERVICE',
  DATABASE: 'DATABASE',
  REDIS_CLUSTER: 'REDIS_CLUSTER',
  FILE_STORAGE: 'FILE_STORAGE'
};



// Permission maps by resource type
export const WRITE_PERMISSIONS = {
  FRONTEND_APP: [
    'update:env_vars',
    'update:branch_env_vars',
    'change:build_spec',
    'create:branch',
    'delete:branch',
    'update:branch',
  ],
  BACKEND_SERVICE: [
    'start:service',
    'stop:service',
    'update:service_env_vars',
    'download:service_env_vars',
    'view:service_env_vars',
    'trigger:build',
    'run:cw_log_insight_query',
    'set:alarm',
    'update:alarm',
    'delete:alarm',
  ],
  DATABASE: [
    'update:connection_params',
    'create:backup',
    'restore:backup'
  ],
  REDIS_CLUSTER: [
    'flush:cache',
    'update:config',
    'restart:cluster'
  ],
  FILE_STORAGE: [
    'upload:file',
    'delete:file',
    'create:folder',
    'delete:folder'
  ]
};

export const READ_PERMISSIONS = {
  FRONTEND_APP: [
    'view:frontend_app',
    'view:logs',
    'view:metrics'
  ],
  BACKEND_SERVICE: [
    'view:backend_service',
    'view:metrics',
    'view:logs',
  ],
  DATABASE: [
    'view:database',
    'view:metrics'
  ],
  REDIS_CLUSTER: [
    'view:redis_cluster',
    'view:metrics'
  ],
  FILE_STORAGE: [
    'view:files',
    'view:metrics'
  ]
};


// Role-based permissions
const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: {
    hasAllPermissions: true
  },
  [ROLES.DEVELOPER]: {
    globalPermissions: [
      'view:projects',
      'create:project',
      'delete:project',
      'update:project',
      'view:project',
      'create:backend_service',
      'delete:backend_service',
      'update:backend_service',
      'create:frontend_app',
      'delete:frontend_app',
      'update:frontend_app',
    ]
  },
  [ROLES.VIEWER]: {
    globalPermissions: [
      'view:project',
      'view:projects'
    ]
  }
};

export const PERMISSION_DESCRIPTIONS = {
  [RESOURCE_LEVEL_PERMISSIONS.READ]: 'View resource details, logs, and metrics',
  [RESOURCE_LEVEL_PERMISSIONS.WRITE]: 'Perform specific actions on the resource',
  [RESOURCE_LEVEL_PERMISSIONS.ADMIN]: 'Full control over the resource',
  ...Object.entries(WRITE_PERMISSIONS).reduce((acc, [resourceType, perms]) => {
    perms.forEach(perm => {
      acc[perm] = `Permission to ${perm.replace(':', ' ')} on ${resourceType.toLowerCase().replace('_', ' ')}`;
    });
    return acc;
  }, {})
};

// Updated authorization check function
export const isAuthorized = (user, projectId, resourceId, resourceType, requiredPermission) => {
  if (!user) return false;
  
  // Admins have all permissions
  if (user.role === ROLES.ADMIN) return true;
  
  // Check project permissions
  if (projectId && user.projectPermissions) {
    const projectPerms = user.projectPermissions[projectId];
    
    if (projectPerms) {
      // If user has project-level admin role
      if (projectPerms.role === 'admin') return true;
      
      // If checking resource-specific permissions
      if (resourceId && projectPerms.resources) {
        const resourcePerms = projectPerms.resources[resourceId];
        
        if (resourcePerms) {
          // Resource admin has all permissions for that resource
          if (resourcePerms === RESOURCE_LEVEL_PERMISSIONS.ADMIN) return true;
          
          // Check specific permissions
          if (Array.isArray(resourcePerms) && resourcePerms.includes(requiredPermission)) {
            return true;
          }
          
          // Check if user has any write permissions, and the required permission is a read permission
          if (Array.isArray(resourcePerms)) {
            // Check if user has any write permissions for this resource type
            const hasWritePermissions = resourcePerms.some(perm => 
              WRITE_PERMISSIONS[resourceType]?.includes(perm)
            );
            
            // If user has write permissions and is requesting a read permission, allow it
            if (hasWritePermissions && READ_PERMISSIONS[resourceType]?.includes(requiredPermission)) {
              return true;
            }
          }
          
          // if (resourcePerms === RESOURCE_LEVEL_PERMISSIONS.WRITE &&
          //     WRITE_PERMISSIONS[resourceType]?.includes(requiredPermission)) {
          //   return true;
          // }
          
          if (resourcePerms === RESOURCE_LEVEL_PERMISSIONS.READ &&
              READ_PERMISSIONS[resourceType]?.includes(requiredPermission)) {
            return true;
          }
        }
      }
    }
  }
  
  // Check if the role has global permissions for this action
  const rolePermissions = ROLE_PERMISSIONS[user.role];
  if (rolePermissions?.globalPermissions?.includes(requiredPermission)) {
    return true;
  }
  
  return false;
};